import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { 
  MagnifyingGlass, 
  Funnel, 
  ArrowClockwise, 
  DownloadSimple, 
  X, 
  Trash, 
  FileCsv,
  CaretUp,
  CaretDown,
  Eye,
  EyeSlash
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import XLSX from 'xlsx-js-style';
import { useAuth } from '../../contexts/AuthContext';
import AdminChat from '../../pages/AdminChat';
import HandlerChat from '../../pages/HandlerChat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_EXCEL_COLUMNS = [
  { id: 'index', label: '#', visible: true },
  { id: 'client_code', label: 'Client Code', visible: true },
  { id: 'username', label: 'Username', visible: true },
  { id: 'mb', label: 'MB', visible: true },
  { id: 'type', label: 'Type', visible: true },
  { id: 'amount', label: 'Amount', visible: true },
  { id: 'account_no', label: 'Account No', visible: true },
  { id: 'ifsc_code', label: 'IFSC Code', visible: true },
  { id: 'account_holder', label: 'Account Holder', visible: true },
  { id: 'date', label: 'Date (IST)', visible: true },
  { id: 'time', label: 'Time (IST)', visible: true },
  { id: 'status', label: 'Status', visible: true },
  { id: 'utr', label: 'UTR', visible: true },
  { id: 'reference', label: 'Reference', visible: true },
  { id: 'received_from', label: 'Received From (Client Account)', visible: true },
  { id: 'received_in', label: 'Received In (Our Account)', visible: true },
  { id: 'paid_to', label: 'Paid To (Client Account)', visible: true },
  { id: 'paid_from', label: 'Paid From (Our Account)', visible: true },
  { id: 'description', label: 'Description', visible: true },
  { id: 'client_address', label: 'Client Address From IFSC', visible: true },
  { id: 'start_time', label: 'Created (Customer)', visible: true },
  { id: 'request_time', label: 'First Reply (Handler)', visible: true },
  { id: 'approved_time', label: 'Closed', visible: true },
  { id: 'tb1', label: 'TB1', visible: true },
  { id: 'tb2', label: 'TB2', visible: true },
  { id: 'total_time', label: 'Total Time', visible: true },
];

export default function Reports({ onClose, onBack }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const handleClose = onClose || onBack || (() => {});

  const isSuperAdmin = user?.role === 'super_admin';
  const userAssignedMBs = useMemo(() => {
    if (Array.isArray(user?.assigned_mbs)) return user.assigned_mbs;
    return ['All'];
  }, [user?.assigned_mbs]);

  const hasAllMBAccess = useMemo(() => {
    if (isSuperAdmin) return true;
    return userAssignedMBs.some(m => String(m).toUpperCase() === 'ALL');
  }, [isSuperAdmin, userAssignedMBs]);

  // Standalone Chat Overlay State
  const [selectedChatRoomId, setSelectedChatRoomId] = useState(null);

  // Filters States
  const [status, setStatus] = useState('Deposit + Withdraw');
  const [dateRange, setDateRange] = useState('Today');
  const [customMode, setCustomMode] = useState('single'); // 'single' | 'range'
  const [singleDate, setSingleDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [segment, setSegment] = useState('ALL MB');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [availableSegments, setAvailableSegments] = useState(['ALL MB', 'MB1', 'MB2', 'MB3', 'MB4', 'MB5', 'MB6']);

  useEffect(() => {
    fetchMBConfig();
  }, [hasAllMBAccess, userAssignedMBs]);

  const fetchMBConfig = async () => {
    try {
      const res = await axios.get(`${API}/mb-config`);
      if (res.data?.mbs) {
        const activeMBs = res.data.mbs.filter(m => !m.hidden).map(m => m.id);
        if (hasAllMBAccess) {
          setAvailableSegments(['ALL MB', ...activeMBs]);
        } else {
          const filteredActive = activeMBs.filter(mbId =>
            userAssignedMBs.some(m => String(m).toUpperCase() === String(mbId).toUpperCase())
          );
          if (filteredActive.length > 0) {
            setAvailableSegments(filteredActive.length > 1 ? ['ALL MB', ...filteredActive] : filteredActive);
            setSegment(filteredActive[0]);
          } else {
            setAvailableSegments([]);
            setSegment('');
          }
        }
      }
    } catch (err) {
      console.error('Failed to load MB config in Reports:', err);
    }
  };

  // Excel Export Modal Customization States
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [excelColumns, setExcelColumns] = useState(() => DEFAULT_EXCEL_COLUMNS.map(col => ({ ...col })));

  // Reset Excel columns to default whenever modal opens
  useEffect(() => {
    if (showExcelModal) {
      setExcelColumns(DEFAULT_EXCEL_COLUMNS.map(col => ({ ...col })));
    }
  }, [showExcelModal]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch transactions from API
  const fetchTransactions = async () => {
    if (status === 'Inquiry' || status === 'inquiry') {
      setTransactions([]);
      setLastSyncTime(new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }).toLowerCase());
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      if (dateRange) params.append('dateRange', dateRange);
      if (dateRange === 'Custom') {
        if (customMode === 'single') {
          if (singleDate) {
            params.append('startDate', singleDate);
            params.append('endDate', singleDate);
          }
        } else {
          if (startDate) params.append('startDate', startDate);
          if (endDate) params.append('endDate', endDate);
        }
      }
      if (segment && segment !== 'ALL MB') params.append('segment', segment);
      if (debouncedSearch) params.append('search', debouncedSearch);

      const response = await axios.get(`${API}/admin/reports/transactions?${params.toString()}`);
      setTransactions(response.data || []);
      setLastSyncTime(new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }).toLowerCase());
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Failed to load transaction reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [status, dateRange, customMode, singleDate, startDate, endDate, segment, debouncedSearch]);

  const handleClearRange = () => {
    setDateRange('Today');
    setCustomMode('single');
    setSingleDate('');
    setStartDate('');
    setEndDate('');
    setStatus('Deposit + Withdraw');
    setSegment('ALL MB');
    setSearch('');
    toast.info('Filters cleared');
  };

  // Filter transactions for display & export based on status and handler MB assignments
  const displayedTransactions = useMemo(() => {
    if (status === 'Inquiry' || status === 'inquiry') return [];
    if (hasAllMBAccess) return transactions;
    return transactions.filter(t => {
      if (!t.mb_setup) return true;
      const mbUpper = String(t.mb_setup).toUpperCase();
      return userAssignedMBs.some(m => String(m).toUpperCase() === mbUpper);
    });
  }, [transactions, status, hasAllMBAccess, userAssignedMBs]);

  // Compute Summary metrics
  const summary = displayedTransactions.reduce((acc, t) => {
    const st = (t.status || '').toUpperCase();
    const type = (t.type || '').toUpperCase();
    const amt = parseFloat(t.amount) || 0;

    if (type === 'DEPOSIT') {
      acc.deposit.amount += amt;
      acc.deposit.count += 1;
    }

    if (st === 'SUCCESS' || st === 'CLOSED' || st === 'DONE' || st === 'SOLVED' || st === 'PAID') {
      acc.success.amount += amt;
      acc.success.count += 1;
    } else if (st === 'PENDING') {
      acc.pending.amount += amt;
      acc.pending.count += 1;
    } else if (st === 'CANCELLED' || st === 'REJECTED') {
      acc.cancelled.amount += amt;
      acc.cancelled.count += 1;
    }
    return acc;
  }, {
    deposit: { amount: 0, count: 0 },
    success: { amount: 0, count: 0 },
    pending: { amount: 0, count: 0 },
    cancelled: { amount: 0, count: 0 }
  });

  // Calculate Turnaround Times (Format strictly in total minutes: 0 min, 1 min, 90 min, etc.)
  const formatDiffMins = (ms) => {
    if (ms === undefined || ms === null || isNaN(ms) || ms < 0) return '-';
    const totalSecs = Math.floor(ms / 1000);
    const mins = Math.floor(totalSecs / 60);
    return `${mins} min`;
  };

  const formatDateTime = (ts) => {
    if (!ts) return '-';
    try {
      const d = new Date(ts);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const timeStr = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }).toLowerCase();
      return `${dd}/${mm} ${timeStr}`;
    } catch {
      return '-';
    }
  };

  // Date Format Helpers for Excel matching reference CSV (e.g. 02-Sep-2026)
  const formatDateIST = (ts) => {
    if (!ts) return '-';
    try {
      const d = new Date(ts);
      const day = String(d.getDate()).padStart(2, '0');
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const month = monthNames[d.getMonth()];
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    } catch {
      return '-';
    }
  };

  const formatTimeIST = (ts) => {
    if (!ts) return '-';
    try {
      const d = new Date(ts);
      return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    } catch {
      return '-';
    }
  };

  const formatDateTime24 = (ts) => {
    if (!ts) return '-';
    try {
      const d = new Date(ts);
      const day = String(d.getDate()).padStart(2, '0');
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const month = monthNames[d.getMonth()];
      const year = d.getFullYear();
      const hrs = String(d.getHours()).padStart(2, '0');
      const mins = String(d.getMinutes()).padStart(2, '0');
      return `${day}-${month}-${year} ${hrs}:${mins}`;
    } catch {
      return '-';
    }
  };

  const formatShortDateTime = (ts) => {
    if (!ts) return '-';
    try {
      const d = new Date(ts);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const timeStr = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
      return `${day}-${month} ${timeStr}`;
    } catch {
      return '-';
    }
  };

  // TB1: Create -> First Reply (request_time - start_time)
  const getTB1 = (t) => {
    const s = t.start_time || t.created_at;
    const r = t.request_time;
    if (!s || !r) return '-';
    const diff = new Date(r) - new Date(s);
    return formatDiffMins(diff);
  };

  // TB2: First Reply -> Close (approved_time - request_time)
  const getTB2 = (t) => {
    const r = t.request_time;
    const a = t.approved_time;
    if (!r || !a) return '-';
    const diff = new Date(a) - new Date(r);
    return formatDiffMins(diff);
  };

  // Total Time: Create -> Close (approved_time - start_time)
  const getTotalTime = (t) => {
    const s = t.start_time || t.created_at;
    const a = t.approved_time;
    if (!s || !a) return '-';
    const diff = new Date(a) - new Date(s);
    return formatDiffMins(diff);
  };

  // PDF Export
  const handleDownloadPDF = () => {
    if (displayedTransactions.length === 0) {
      toast.error('No transactions to export');
      return;
    }

    const doc = new jsPDF('landscape');
    
    doc.setFontSize(16);
    doc.setTextColor(21, 101, 192);
    doc.text(`Transaction Report - ${status} (${segment})`, 14, 18);
    
    doc.setTextColor(60, 60, 60);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Generated at: ${lastSyncTime} | Segment: ${segment} | Filter: ${status} | Date Range: ${dateRange}`, 14, 26);

    const tableData = displayedTransactions.map((t, idx) => {
      const reqT = t.request_time ? new Date(t.request_time).toLocaleDateString('en-GB') + ' ' + new Date(t.request_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : '-';
      const appT = t.approved_time ? new Date(t.approved_time).toLocaleDateString('en-GB') + ' ' + new Date(t.approved_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : '-';
      
      return [
        idx + 1,
        t.system_username || '-',
        t.username ? `${t.username} (${t.mb_setup || 'ALL MB'})` : '-',
        t.type,
        `Rs. ${t.amount}`,
        t.account_no || '-',
        t.ifsc_code || '-',
        t.account_holder || '-',
        new Date(t.created_at).toLocaleDateString('en-GB'),
        new Date(t.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
        t.status,
        t.utr || '-',
        t.reference || '-',
        t.received_from || '-',
        t.received_in || '-',
        t.paid_to || '-',
        t.paid_from || '-',
        t.description || '-',
        t.client_address || '-',
        reqT,
        appT,
        getTB1(t),
        getTB2(t),
        getTotalTime(t)
      ];
    });

    autoTable(doc, {
      startY: 32,
      head: [[
        '#', 'Client Code', 'Username', 'Type', 'Amount', 'Account No', 'IFSC Code', 'Account Holder', 'Date', 'Time', 'Status', 'UTR', 'Reference',
        'Recv From', 'Recv In', 'Paid To', 'Paid From', 'Desc', 'Address',
        'First Reply', 'Closed', 'TB1', 'TB2', 'Total Time'
      ]],
      body: tableData,
      theme: 'grid',
      styles: { fontSize: 5.8, cellPadding: 1 },
      headStyles: { fillColor: [40, 167, 69] }
    });

    doc.save(`withdraw_report_${segment.replace(/\s+/g, '_')}_${Date.now()}.pdf`);
    toast.success('PDF downloaded successfully!');
  };

  // ─── EXCEL COLUMN CUSTOMIZATION & EXPORT LOGIC ──────────────────────────────
  const moveColumnUp = (idx) => {
    if (idx <= 0) return;
    const updated = [...excelColumns];
    const temp = updated[idx - 1];
    updated[idx - 1] = updated[idx];
    updated[idx] = temp;
    setExcelColumns(updated);
  };

  const moveColumnDown = (idx) => {
    if (idx >= excelColumns.length - 1) return;
    const updated = [...excelColumns];
    const temp = updated[idx + 1];
    updated[idx + 1] = updated[idx];
    updated[idx] = temp;
    setExcelColumns(updated);
  };

  const toggleColumnVisibility = (idx) => {
    const updated = [...excelColumns];
    updated[idx].visible = !updated[idx].visible;
    setExcelColumns(updated);
  };

  // Turnaround mins integer calculations for native Excel Number cells
  const getTB1Mins = (t) => {
    const s = t.start_time || t.created_at;
    const r = t.request_time;
    if (!s || !r) return null;
    const diff = new Date(r) - new Date(s);
    if (isNaN(diff) || diff < 0) return null;
    return Math.floor(diff / 60000);
  };

  const getTB2Mins = (t) => {
    const r = t.request_time;
    const a = t.approved_time;
    if (!r || !a) return null;
    const diff = new Date(a) - new Date(r);
    if (isNaN(diff) || diff < 0) return null;
    return Math.floor(diff / 60000);
  };

  const getTotalTimeMins = (t) => {
    const s = t.start_time || t.created_at;
    const a = t.approved_time;
    if (!s || !a) return null;
    const diff = new Date(a) - new Date(s);
    if (isNaN(diff) || diff < 0) return null;
    return Math.floor(diff / 60000);
  };

  const getExcelCell = (t, colId, index) => {
    switch (colId) {
      case 'index':
        return { v: index + 1, t: 'n' };
      
      case 'client_code': {
        const num = parseInt(t.system_username, 10);
        return (!isNaN(num) && String(num) === String(t.system_username).trim())
          ? { v: num, t: 'n', z: '0' }
          : { v: t.system_username || '-', t: 's' };
      }

      case 'username':
        return { v: t.username || '-', t: 's' };

      case 'mb':
        return { v: t.mb_setup || 'ALL MB', t: 's' };

      case 'type': {
        const formattedType = t.type === 'WITHDRAW' ? 'Withdraw' : (t.type === 'DEPOSIT' ? 'Deposit' : 'Support');
        return { v: formattedType, t: 's' };
      }

      case 'amount': {
        if (t.type === 'SUPPORT') return { v: '-', t: 's' };
        const amt = parseFloat(t.amount);
        return !isNaN(amt)
          ? { v: amt, t: 'n', z: '"₹"#,##0' }
          : { v: t.amount || 0, t: 'n', z: '"₹"#,##0' };
      }

      case 'account_no':
        return { v: t.account_no || '-', t: 's' };

      case 'ifsc_code':
        return { v: t.ifsc_code || '-', t: 's' };

      case 'account_holder':
        return { v: t.account_holder || '-', t: 's' };

      case 'date': {
        const d = t.created_at ? new Date(t.created_at) : null;
        return (d && !isNaN(d.getTime()))
          ? { v: d, t: 'd', z: 'dd-mmm-yyyy' }
          : { v: '-', t: 's' };
      }

      case 'time':
        return { v: formatTimeIST(t.created_at), t: 's' };

      case 'status': {
        const st = (t.status || '').toUpperCase();
        const formattedStatus = (st === 'SUCCESS' || st === 'DONE' || st === 'CLOSED' || st === 'PAID') ? 'Paid' : (st === 'PENDING' ? 'Pending' : t.status || '-');
        return { v: formattedStatus, t: 's' };
      }

      case 'utr': {
        const utrNum = Number(t.utr);
        return (t.utr && !isNaN(utrNum) && /^\d+$/.test(String(t.utr).trim()))
          ? { v: utrNum, t: 'n', z: '0' }
          : { v: t.utr || '-', t: 's' };
      }

      case 'reference':
        return { v: t.reference || '-', t: 's' };

      case 'received_from':
        return { v: t.received_from || '-', t: 's' };

      case 'received_in':
        return { v: t.received_in || '-', t: 's' };

      case 'paid_to':
        return { v: t.paid_to || '-', t: 's' };

      case 'paid_from':
        return { v: t.paid_from || '-', t: 's' };

      case 'description':
        return { v: t.description || '-', t: 's' };

      case 'client_address':
        return { v: t.client_address || '-', t: 's' };

      case 'start_time': {
        const ts = t.start_time || t.created_at;
        const d = ts ? new Date(ts) : null;
        return (d && !isNaN(d.getTime()))
          ? { v: d, t: 'd', z: 'dd-mmm-yyyy hh:mm' }
          : { v: '-', t: 's' };
      }

      case 'request_time': {
        const d = t.request_time ? new Date(t.request_time) : null;
        return (d && !isNaN(d.getTime()))
          ? { v: d, t: 'd', z: 'dd-mmm-yyyy hh:mm' }
          : { v: '-', t: 's' };
      }

      case 'approved_time': {
        const d = t.approved_time ? new Date(t.approved_time) : null;
        return (d && !isNaN(d.getTime()))
          ? { v: d, t: 'd', z: 'dd-mmm-yyyy hh:mm' }
          : { v: '-', t: 's' };
      }

      case 'tb1': {
        const mins = getTB1Mins(t);
        return mins !== null ? { v: mins, t: 'n', z: '0" min"' } : { v: '-', t: 's' };
      }

      case 'tb2': {
        const mins = getTB2Mins(t);
        return mins !== null ? { v: mins, t: 'n', z: '0" min"' } : { v: '-', t: 's' };
      }

      case 'total_time': {
        const mins = getTotalTimeMins(t);
        return mins !== null ? { v: mins, t: 'n', z: '0" min"' } : { v: '-', t: 's' };
      }

      default:
        return { v: '-', t: 's' };
    }
  };

  const getPreviewCellValue = (t, colId, index) => {
    const cell = getExcelCell(t, colId, index);
    if (!cell || cell.v === undefined || cell.v === null) return '-';
    if (cell.t === 'd' && cell.v instanceof Date) {
      if (colId === 'date') return formatDateIST(cell.v);
      return formatDateTime24(cell.v);
    }
    if (colId === 'amount' && cell.t === 'n') return `₹${cell.v.toLocaleString('en-IN')}`;
    if ((colId === 'tb1' || colId === 'tb2' || colId === 'total_time') && cell.t === 'n') return `${cell.v} min`;
    return String(cell.v);
  };

  const executeExcelDownload = () => {
    try {
      const activeCols = excelColumns.filter(c => c.visible);
      if (activeCols.length === 0) {
        toast.error("Select at least one column for Excel export");
        return;
      }
      const headers = activeCols.map(c => c.label);
      const dataRows = displayedTransactions.map((t, index) => {
        return activeCols.map(c => getExcelCell(t, c.id, index));
      });

      // Summary totals
      const totalDeposits = summary.deposit.amount;
      const totalWithdraws = displayedTransactions.reduce((sum, t) => {
        const type = (t.type || '').toUpperCase();
        const st = (t.status || '').toUpperCase();
        const isWithdraw = type === 'WITHDRAW' || type === 'WITHDRAWAL';
        const isSuccess = ['SUCCESS', 'CLOSED', 'DONE', 'SOLVED', 'PAID'].includes(st);
        if (isWithdraw && isSuccess) {
          return sum + (parseFloat(t.amount) || 0);
        }
        return sum;
      }, 0);
      const totalPending = summary.pending.amount;
      const totalCancelled = summary.cancelled.amount;
      const netTotal = totalDeposits - totalWithdraws - totalCancelled;

      // Footer summary rows with cell formatting
      const summaryRows = [
        [],
        [{ v: 'Total Deposits', t: 's' }, { v: totalDeposits, t: 'n', z: '"₹"#,##0' }],
        [{ v: 'Total Withdraws (Success)', t: 's' }, { v: totalWithdraws, t: 'n', z: '"₹"#,##0' }],
        [{ v: 'Total Pending', t: 's' }, { v: totalPending, t: 'n', z: '"₹"#,##0' }],
        [{ v: 'NET (Deposits − Success − Cancelled)', t: 's' }, { v: netTotal, t: 'n', z: '"₹"#,##0' }],
      ];

      const allRows = [headers, ...dataRows, ...summaryRows];

      // Build worksheet & workbook using SheetJS (XLSX)
      const worksheet = XLSX.utils.aoa_to_sheet(allRows, { cellDates: true });

      // Format header row (Row 1): Dark Green background (#1B5E20), bold white text, center alignment
      headers.forEach((_, i) => {
        const cellRef = XLSX.utils.encode_cell({ r: 0, c: i });
        if (worksheet[cellRef]) {
          worksheet[cellRef].s = {
            fill: {
              patternType: 'solid',
              fgColor: { rgb: '1B5E20' },
            },
            font: {
              name: 'Calibri',
              sz: 11,
              bold: true,
              color: { rgb: 'FFFFFF' },
            },
            alignment: {
              horizontal: 'center',
              vertical: 'center',
              wrapText: true,
            },
          };
        }
      });

      // Set dynamic column widths
      const colWidths = headers.map((h, i) => {
        let maxLen = String(h).length;
        dataRows.forEach(row => {
          const cellObj = row[i];
          const valLen = String(cellObj?.v || '').length;
          if (valLen > maxLen) maxLen = valLen;
        });
        return { wch: Math.min(Math.max(maxLen + 3, 12), 45) };
      });
      worksheet['!cols'] = colWidths;

      // Freeze header row (Row 1) so header stays fixed when scrolling data in Excel
      worksheet['!views'] = [
        {
          state: 'frozen',
          xSplit: 0,
          ySplit: 1,
          topLeftCell: 'A2',
          activePane: 'bottomLeft'
        }
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Withdraw Report");

      // Write native .xlsx file
      const fileName = `withdraw_report_${segment.replace(/\s+/g, '_')}_${Date.now()}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      toast.success('Excel (.xlsx) downloaded successfully!');
      setShowExcelModal(false);
    } catch (err) {
      toast.error('Failed to generate Excel file');
      console.error(err);
    }
  };

  return (
    <div className="flex flex-col h-full bg-[#f8fafc] border border-gray-200 rounded-xl overflow-hidden shadow-2xl text-[13px] text-gray-800">
      
      {/* ─── Blue Header Bar ──────────────────────────────────────────────────── */}
      <div className="bg-[#1565C0] text-white px-3 sm:px-5 py-2.5 sm:py-3.5 flex items-center justify-between flex-shrink-0 gap-2">
        <h2 className="text-[14px] sm:text-[16px] font-bold tracking-wide truncate">Withdraw Report (All Clients)</h2>
        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
          <button
            onClick={() => {
              if (displayedTransactions.length === 0) {
                toast.error('No transactions to export');
                return;
              }
              setShowExcelModal(true);
            }}
            className="flex items-center gap-1 bg-[#2E7D32] hover:bg-[#1B5E20] text-white text-xs font-bold px-2.5 py-1.5 rounded shadow transition-all active:scale-95"
            title="Download Excel"
            data-testid="header-excel-export-btn"
          >
            <FileCsv size={16} weight="bold" />
            <span className="hidden xs:inline sm:inline">Excel</span>
          </button>
          <button
            onClick={handleDownloadPDF}
            className="flex items-center gap-1 bg-[#D32F2F] hover:bg-[#B71C1C] text-white text-xs font-bold px-2.5 py-1.5 rounded shadow transition-all active:scale-95"
            title="Download PDF"
            data-testid="header-pdf-export-btn"
          >
            <DownloadSimple size={16} weight="bold" />
            <span className="hidden xs:inline sm:inline">PDF</span>
          </button>
          <button 
            onClick={handleClose}
            className="p-1 hover:bg-white/10 rounded transition-colors text-white focus:outline-none ml-1"
          >
            <X size={20} weight="bold" />
          </button>
        </div>
      </div>

      {/* ─── Filters Panel ────────────────────────────────────────────────────── */}
      <div className="p-2.5 sm:p-4 bg-white border-b border-gray-200 space-y-2 sm:space-y-3.5 flex-shrink-0 shadow-sm">
        
        {/* Row 1: Type / Status filters */}
        <div className="flex flex-wrap gap-1.5 sm:gap-2.5">
          {['All', 'Success', 'Pending', 'Cancelled', 'Deposit', 'Deposit + Withdraw', 'Inquiry'].map(btn => (
            <button
              key={btn}
              onClick={() => setStatus(btn)}
              className={`px-2 py-1 sm:px-3 sm:py-1.5 rounded text-[11px] sm:text-[12px] font-semibold border transition-all ${
                status === btn 
                  ? 'bg-[#1565C0] text-white border-[#1565C0] shadow-sm'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {btn}
            </button>
          ))}
        </div>

        {/* Row 2: Date filters + Refresh */}
        <div className="flex flex-col gap-1.5 sm:gap-2.5">
          <div className="flex items-center justify-between gap-2 sm:gap-3 flex-wrap">
            <div className="flex flex-wrap gap-1.5 sm:gap-2 items-center">
              {['Today', 'Yesterday', 'Weekly', 'Monthly', 'Custom'].map(btn => (
                <button
                  key={btn}
                  onClick={() => setDateRange(btn)}
                  className={`px-2.5 py-1 sm:px-3.5 sm:py-1.5 rounded-md text-[11px] sm:text-[12px] font-semibold border transition-all ${
                    dateRange === btn 
                      ? 'bg-[#8E24AA] text-white border-[#8E24AA] shadow-sm'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {btn}
                </button>
              ))}
            </div>

            {/* Refresh Action */}
            <button
              onClick={fetchTransactions}
              disabled={loading}
              className="flex items-center gap-1 sm:gap-1.5 text-[11px] sm:text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 font-bold px-2 py-1 sm:px-3 sm:py-1.5 rounded-lg transition-colors"
            >
              <ArrowClockwise size={13} className={loading ? 'animate-spin' : ''} />
              <span>Refresh</span>
              {lastSyncTime && <span className="font-normal text-gray-500 ml-1">{lastSyncTime}</span>}
            </button>
          </div>

          {/* Sub-row for Custom Date Mode: Single date vs From – To */}
          {dateRange === 'Custom' && (
            <div className="flex flex-col gap-2 transition-all">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCustomMode('single')}
                  className={`px-2.5 py-1 sm:px-3.5 sm:py-1.5 rounded-md text-[11px] sm:text-[12px] font-semibold border transition-all ${
                    customMode === 'single'
                      ? 'bg-[#8E24AA] text-white border-[#8E24AA] shadow-sm'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  Single date
                </button>
                <button
                  onClick={() => setCustomMode('range')}
                  className={`px-2.5 py-1 sm:px-3.5 sm:py-1.5 rounded-md text-[11px] sm:text-[12px] font-semibold border transition-all ${
                    customMode === 'range'
                      ? 'bg-[#8E24AA] text-white border-[#8E24AA] shadow-sm'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  From – To
                </button>
              </div>

              {/* Custom Date Inputs */}
              {customMode === 'single' ? (
                <div className="max-w-xs sm:max-w-sm w-full">
                  <input
                    type="date"
                    value={singleDate}
                    onChange={(e) => setSingleDate(e.target.value)}
                    className="w-full px-2.5 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[#8E24AA] bg-white text-gray-800 shadow-sm"
                  />
                </div>
              ) : (
                <div className="flex items-center gap-2 max-w-md sm:max-w-lg w-full">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="flex-1 px-2.5 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[#8E24AA] bg-white text-gray-800 shadow-sm"
                  />
                  <span className="text-gray-500 text-xs font-medium px-1">to</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="flex-1 px-2.5 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[#8E24AA] bg-white text-gray-800 shadow-sm"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Row 3: MB setup filters */}
        <div className="flex items-center flex-wrap gap-1.5 sm:gap-2">
          <span className="text-[11px] sm:text-xs font-bold text-gray-500 mr-1">MB:</span>
          {availableSegments.map(btn => (
            <button
              key={btn}
              onClick={() => setSegment(btn)}
              className={`px-2 py-0.5 sm:px-3 sm:py-1 rounded-md text-[10px] sm:text-[11px] font-bold border transition-all ${
                segment === btn 
                  ? 'bg-[#8E24AA] text-white border-[#8E24AA] shadow-sm'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {btn}
            </button>
          ))}
        </div>

        {/* Row 4: Search input */}
        <div className="relative">
          <MagnifyingGlass className="absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
          <input
            type="text"
            placeholder="Search client name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 sm:pl-9 pr-3 sm:pr-4 py-1.5 sm:py-2 border border-gray-200 rounded-lg bg-gray-50/50 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all placeholder-gray-400"
          />
        </div>
      </div>

      {/* ─── Dynamic Table View ────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-x-auto overflow-y-auto bg-white min-h-[160px] sm:min-h-0">
        {loading && displayedTransactions.length === 0 ? (
          <div className="h-full flex items-center justify-center text-gray-500 gap-2">
            <ArrowClockwise size={20} className="animate-spin text-blue-600" />
            <span>Syncing records...</span>
          </div>
        ) : displayedTransactions.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-gray-400 py-16">
            <Funnel size={36} weight="light" className="mb-2 text-gray-300" />
            <p className="font-semibold text-sm">No transaction records found</p>
            <p className="text-xs text-gray-400 mt-1">Try adjusting your filters or date range</p>
          </div>
        ) : (
          <table className="w-full border-collapse text-left text-[11px] whitespace-nowrap">
            {/* Headers */}
            <thead className="bg-[#1b5e20] text-white sticky top-0 z-10 shadow-sm uppercase tracking-wider font-bold">
              <tr>
                <th className="px-3 py-3 border-r border-green-700/30 text-center">#</th>
                <th className="px-3 py-3 border-r border-green-700/30 text-center font-bold text-[#e8f5e9]">Client Code</th>
                <th className="px-3 py-3 border-r border-green-700/30">Username</th>
                <th className="px-3 py-3 border-r border-green-700/30 text-center">Type</th>
                <th className="px-3 py-3 border-r border-green-700/30 text-right">Amount</th>
                <th className="px-3 py-3 border-r border-green-700/30 font-bold text-[#e8f5e9]">Account No</th>
                <th className="px-3 py-3 border-r border-green-700/30 font-bold text-[#e8f5e9]">IFSC Code</th>
                <th className="px-3 py-3 border-r border-green-700/30 font-bold text-[#e8f5e9]">Account Holder</th>
                <th className="px-3 py-3 border-r border-green-700/30">Date</th>
                <th className="px-3 py-3 border-r border-green-700/30">Time</th>
                <th className="px-3 py-3 border-r border-green-700/30 text-center">Status</th>
                <th className="px-3 py-3 border-r border-green-700/30">UTR</th>
                <th className="px-3 py-3 border-r border-green-700/30">Reference</th>
                <th className="px-3 py-3 border-r border-green-700/30 flex-col">
                  <div>Received From</div>
                  <div className="text-[8px] font-normal text-green-200 capitalize">Client Account</div>
                </th>
                <th className="px-3 py-3 border-r border-green-700/30">
                  <div>Received In</div>
                  <div className="text-[8px] font-normal text-green-200 capitalize">Our Account</div>
                </th>
                <th className="px-3 py-3 border-r border-green-700/30">
                  <div>Paid To</div>
                  <div className="text-[8px] font-normal text-green-200 capitalize">Client Account</div>
                </th>
                <th className="px-3 py-3 border-r border-green-700/30">
                  <div>Paid From</div>
                  <div className="text-[8px] font-normal text-green-200 capitalize">Our Account</div>
                </th>
                <th className="px-3 py-3 border-r border-green-700/30">Description</th>
                <th className="px-3 py-3 border-r border-green-700/30">
                  <div>Client Address</div>
                  <div className="text-[8px] font-normal text-green-200 capitalize">from IFSC</div>
                </th>
                <th className="px-3 py-3 border-r border-green-700/30">
                  <div>Created</div>
                  <div className="text-[8px] font-normal text-green-200 uppercase">Customer</div>
                </th>
                <th className="px-3 py-3 border-r border-green-700/30">
                  <div>First Reply</div>
                  <div className="text-[8px] font-normal text-green-200 uppercase">Handler</div>
                </th>
                <th className="px-3 py-3 border-r border-green-700/30">
                  <div>Closed</div>
                </th>
                <th className="px-3 py-3 border-r border-green-700/30">
                  <div>TB1</div>
                  <div className="text-[8px] font-normal text-green-200 uppercase">Create-&gt;FirstReply</div>
                </th>
                <th className="px-3 py-3 border-r border-green-700/30">
                  <div>TB2</div>
                  <div className="text-[8px] font-normal text-green-200 uppercase">FirstReply-&gt;Close</div>
                </th>
                <th className="px-3 py-3">
                  <div>Total Time</div>
                  <div className="text-[8px] font-normal text-green-200 uppercase">Create-&gt;Close</div>
                </th>
              </tr>
            </thead>
            
            {/* Body */}
            <tbody className="divide-y divide-gray-200/80 font-medium">
              {displayedTransactions.map((t, idx) => {
                const isWithdraw = t.type === 'WITHDRAW';
                const createdDate = new Date(t.created_at);
                
                const dateStr = createdDate.toLocaleDateString('en-GB'); // DD/MM/YYYY
                const timeStr = createdDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }).toLowerCase();
                
                return (
                  <tr key={t.id} className="hover:bg-gray-50/50 odd:bg-white even:bg-gray-50/20 text-gray-700">
                    <td className="px-3 py-2 border-r border-gray-200 text-center font-normal text-gray-400">{idx + 1}</td>
                    
                    {/* Client Code */}
                    <td className="px-3 py-2 border-r border-gray-200 text-center font-mono font-bold text-gray-800">
                      {t.system_username || '-'}
                    </td>

                    {/* Username + Segment Badge */}
                    <td className="px-3 py-2 border-r border-gray-200 font-bold">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => {
                            if (t.room_id) {
                              setSelectedChatRoomId(t.room_id);
                            } else {
                              toast.error('No chat room available for this user');
                            }
                          }}
                          className="hover:underline text-blue-600 font-bold flex items-center gap-1"
                        >
                          {t.username || 'Client'}
                        </button>
                        {t.mb_setup && (
                          <span className="bg-amber-100 text-amber-800 text-[9px] font-black px-1.5 py-0.5 rounded border border-amber-300">
                            {t.mb_setup}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Type */}
                    <td className="px-3 py-2 border-r border-gray-200 text-center">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                        t.type === 'DEPOSIT' 
                          ? 'bg-green-100 text-green-800' 
                          : t.type === 'WITHDRAW'
                          ? 'bg-orange-100 text-orange-800'
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {t.type === 'WITHDRAW' ? 'WITHDRAW' : (t.type === 'DEPOSIT' ? 'DEPOSIT' : 'SUPPORT')}
                      </span>
                    </td>

                    {/* Amount */}
                    <td className={`px-3 py-2 border-r border-gray-200 text-right font-mono font-bold ${
                      t.type === 'DEPOSIT' ? 'text-green-700' : t.type === 'WITHDRAW' ? 'text-red-700' : 'text-gray-500'
                    }`}>
                      {t.type === 'SUPPORT' ? '-' : `₹${t.amount?.toLocaleString('en-IN') || 0}`}
                    </td>

                    {/* Bank details */}
                    <td className="px-3 py-2 border-r border-gray-200 font-mono">{t.account_no || '-'}</td>
                    <td className="px-3 py-2 border-r border-gray-200 font-mono uppercase">{t.ifsc_code || '-'}</td>
                    <td className="px-3 py-2 border-r border-gray-200">{t.account_holder || '-'}</td>

                    {/* Date & Time */}
                    <td className="px-3 py-2 border-r border-gray-200 font-mono">{dateStr}</td>
                    <td className="px-3 py-2 border-r border-gray-200 font-mono">{timeStr}</td>

                    {/* Status Badge */}
                    <td className="px-3 py-2 border-r border-gray-200 text-center">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                        (t.status || '').toUpperCase() === 'SUCCESS' || (t.status || '').toUpperCase() === 'CLOSED' || (t.status || '').toUpperCase() === 'SOLVED' || (t.status || '').toUpperCase() === 'DONE'
                          ? 'bg-green-800 text-white' 
                          : (t.status || '').toUpperCase() === 'PENDING'
                          ? 'bg-amber-100 text-amber-800 border border-amber-300'
                          : 'bg-red-100 text-red-800 border border-red-300'
                      }`}>
                        {(t.status || 'DONE').toUpperCase()}
                      </span>
                    </td>

                    {/* UTR & Ref */}
                    <td className="px-3 py-2 border-r border-gray-200 font-mono">{t.utr || '-'}</td>
                    <td className="px-3 py-2 border-r border-gray-200 max-w-[120px] truncate" title={t.reference || ''}>{t.reference || '-'}</td>

                    {/* Accounts detail */}
                    <td className="px-3 py-2 border-r border-gray-200">{t.received_from || '-'}</td>
                    <td className="px-3 py-2 border-r border-gray-200">{t.received_in || '-'}</td>
                    <td className="px-3 py-2 border-r border-gray-200">{t.paid_to || '-'}</td>
                    <td className="px-3 py-2 border-r border-gray-200">{t.paid_from || '-'}</td>
                    
                    {/* Description */}
                    <td className="px-3 py-2 border-r border-gray-200 max-w-[150px] truncate" title={t.description || ''}>{t.description || '-'}</td>

                    {/* Address */}
                    <td className="px-3 py-2 border-r border-gray-200 max-w-[150px] truncate uppercase font-medium text-gray-500" title={t.client_address || ''}>{t.client_address || '-'}</td>

                    {/* Timestamps */}
                    <td className="px-3 py-2 border-r border-gray-200 font-mono text-gray-500">{formatShortDateTime(t.start_time || t.created_at)}</td>
                    <td className="px-3 py-2 border-r border-gray-200 font-mono text-gray-500">{formatShortDateTime(t.request_time)}</td>
                    <td className="px-3 py-2 border-r border-gray-200 font-mono text-gray-500">{formatShortDateTime(t.approved_time)}</td>

                    {/* Turnaround times */}
                    <td className="px-3 py-2 border-r border-gray-200 font-mono text-amber-700 font-bold">{getTB1(t)}</td>
                    <td className="px-3 py-2 border-r border-gray-200 font-mono text-blue-700 font-bold">{getTB2(t)}</td>
                    <td className="px-3 py-2 font-mono text-emerald-700 font-extrabold">{getTotalTime(t)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ─── Footer Metric summaries & Actions ────────────────────────────────── */}
      <div className="flex-shrink-0 bg-gray-100 p-2 sm:p-4 border-t border-gray-200 space-y-1.5 sm:space-y-3.5 shadow-inner">
        
        {/* Metric summary boxes */}
        <div className="grid grid-cols-3 gap-1.5 sm:gap-3 md:gap-4">
          <div className="bg-[#E8F5E9] border border-[#A5D6A7] rounded-lg p-1.5 sm:p-2.5 md:p-3 text-center sm:text-left">
            <h4 className="font-bold text-[#2E7D32] uppercase tracking-tight text-[10px] sm:text-xs">Success</h4>
            <p className="text-xs sm:text-base md:text-[18px] font-black text-gray-900 mt-0.5 truncate">₹{summary.success.amount.toLocaleString('en-IN')}</p>
            <p className="text-[9px] sm:text-xs text-[#2E7D32] font-semibold mt-0.5">{summary.success.count} entries</p>
          </div>

          <div className="bg-[#FFFDE7] border border-[#FFF59D] rounded-lg p-1.5 sm:p-2.5 md:p-3 text-center sm:text-left">
            <h4 className="font-bold text-[#F57F17] uppercase tracking-tight text-[10px] sm:text-xs">Pending</h4>
            <p className="text-xs sm:text-base md:text-[18px] font-black text-gray-900 mt-0.5 truncate">₹{summary.pending.amount.toLocaleString('en-IN')}</p>
            <p className="text-[9px] sm:text-xs text-[#F57F17] font-semibold mt-0.5">{summary.pending.count} entries</p>
          </div>

          <div className="bg-[#FFEBEE] border border-[#EF9A9A] rounded-lg p-1.5 sm:p-2.5 md:p-3 text-center sm:text-left">
            <h4 className="font-bold text-[#C62828] uppercase tracking-tight text-[10px] sm:text-xs">Cancelled</h4>
            <p className="text-xs sm:text-base md:text-[18px] font-black text-gray-900 mt-0.5 truncate">₹{summary.cancelled.amount.toLocaleString('en-IN')}</p>
            <p className="text-[9px] sm:text-xs text-[#C62828] font-semibold mt-0.5">{summary.cancelled.count} entries</p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="grid grid-cols-2 sm:flex sm:flex-row items-center gap-1.5 sm:gap-3 pt-1 sm:pt-2">
          <button
            onClick={handleClearRange}
            className="sm:flex-1 bg-[#D32F2F] hover:bg-[#B71C1C] text-white font-bold px-2 py-1.5 sm:px-4 sm:py-2.5 rounded shadow flex items-center justify-center gap-1.5 transition-colors text-xs sm:text-sm"
          >
            <Trash size={14} className="sm:w-4 sm:h-4" weight="bold" />
            Clear Range
          </button>
          
          <button
            onClick={handleClose}
            className="sm:flex-[2] bg-[#37474F] hover:bg-[#263238] text-white font-bold px-2 py-1.5 sm:px-4 sm:py-2.5 rounded shadow flex items-center justify-center gap-1.5 transition-colors text-xs sm:text-sm"
          >
            <X size={14} className="sm:w-4 sm:h-4" weight="bold" />
            Close View
          </button>
          
          <button
            onClick={() => {
              if (displayedTransactions.length === 0) {
                toast.error('No transactions to export');
                return;
              }
              setShowExcelModal(true);
            }}
            className="sm:flex-[2] bg-[#388E3C] hover:bg-[#2E7D32] text-white font-bold px-2 py-1.5 sm:px-4 sm:py-2.5 rounded shadow flex items-center justify-center gap-1.5 transition-colors text-xs sm:text-sm"
            data-testid="excel-export-trigger-btn"
          >
            <FileCsv size={14} className="sm:w-4 sm:h-4" weight="bold" />
            Excel
          </button>
          
          <button
            onClick={handleDownloadPDF}
            className="sm:flex-1 bg-[#D32F2F] hover:bg-[#B71C1C] text-white font-bold px-2 py-1.5 sm:px-4 sm:py-2.5 rounded shadow flex items-center justify-center gap-1.5 transition-colors text-xs sm:text-sm"
          >
            <DownloadSimple size={14} className="sm:w-4 sm:h-4" weight="bold" />
            PDF
          </button>
        </div>
      </div>

      {/* ─── EXCEL EXPORT PREVIEW & CUSTOMIZATION MODAL (Matching Reference Image) ─── */}
      {showExcelModal && (
        <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-3 md:p-6 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden border border-slate-300 font-sans">
            
            {/* Modal Header */}
            <div className="bg-[#1b5e20] text-white px-4 py-3 flex items-center justify-between flex-shrink-0 shadow-md">
              <div>
                <h3 className="text-base font-bold flex items-center gap-2">
                  <FileCsv size={20} weight="bold" />
                  Excel Export — Preview
                </h3>
                <p className="text-[11px] text-green-100 font-normal mt-0.5">
                  Reorder with ↑↓, hide with the eye icon. Preview updates live.
                </p>
              </div>
              <button
                onClick={() => setShowExcelModal(false)}
                className="p-1.5 hover:bg-white/10 rounded-full transition-colors text-white focus:outline-none"
              >
                <X size={20} weight="bold" />
              </button>
            </div>

            {/* Modal Body: Split view (Left: Controls, Right: Live Preview) */}
            <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
              
              {/* Left Column Sidebar */}
              <div className="w-full md:w-80 border-r border-slate-200 bg-slate-50 p-3 flex flex-col flex-shrink-0">
                <div className="flex items-center justify-between mb-2 pb-1 border-b border-slate-200">
                  <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    COLUMNS ({excelColumns.filter(c => c.visible).length}/{excelColumns.length})
                  </span>
                  <button
                    onClick={() => {
                      const allVisible = excelColumns.every(c => c.visible);
                      setExcelColumns(excelColumns.map(c => ({ ...c, visible: !allVisible })));
                    }}
                    className="text-[10px] font-bold text-blue-600 hover:underline"
                  >
                    {excelColumns.every(c => c.visible) ? 'Deselect All' : 'Select All'}
                  </button>
                </div>

                {/* Scrollable Columns List */}
                <div className="flex-1 overflow-y-auto space-y-1.5 pr-1">
                  {excelColumns.map((col, idx) => (
                    <div
                      key={col.id}
                      className={`flex items-center gap-1.5 p-1.5 rounded border text-xs shadow-2xs transition-colors ${
                        col.visible 
                          ? 'bg-white border-slate-300 text-slate-800' 
                          : 'bg-slate-100 border-slate-200 text-slate-400'
                      }`}
                    >
                      {/* Up Arrow */}
                      <button
                        onClick={() => moveColumnUp(idx)}
                        disabled={idx === 0}
                        className="p-1 hover:bg-slate-100 disabled:opacity-30 rounded text-slate-600"
                        title="Move Up"
                      >
                        <CaretUp size={14} weight="bold" />
                      </button>

                      {/* Down Arrow */}
                      <button
                        onClick={() => moveColumnDown(idx)}
                        disabled={idx === excelColumns.length - 1}
                        className="p-1 hover:bg-slate-100 disabled:opacity-30 rounded text-slate-600"
                        title="Move Down"
                      >
                        <CaretDown size={14} weight="bold" />
                      </button>

                      {/* Visibility Toggle Eye Button */}
                      <button
                        onClick={() => toggleColumnVisibility(idx)}
                        className={`p-1 rounded transition-colors ${
                          col.visible 
                            ? 'text-emerald-600 hover:bg-emerald-50' 
                            : 'text-slate-400 hover:bg-slate-200'
                        }`}
                        title={col.visible ? "Hide Column" : "Show Column"}
                      >
                        {col.visible ? <Eye size={16} weight="bold" /> : <EyeSlash size={16} weight="bold" />}
                      </button>

                      {/* Label */}
                      <span className={`flex-1 truncate font-medium ${col.visible ? 'text-slate-800' : 'text-slate-400 line-through'}`}>
                        {col.label.replace('\n', ' ')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Live Preview Panel */}
              <div className="flex-1 p-3.5 flex flex-col overflow-hidden bg-white">
                <p className="text-xs text-slate-500 mb-2 font-medium">
                  Showing first {Math.min(5, displayedTransactions.length)} of {displayedTransactions.length} rows in preview. Full export will include all.
                </p>

                <div className="flex-1 overflow-auto border border-slate-200 rounded-lg shadow-inner bg-slate-50/30">
                  <table className="w-full text-left text-[11px] whitespace-nowrap border-collapse">
                    <thead className="bg-[#2e7d32] text-white sticky top-0 z-10 font-bold uppercase tracking-wider">
                      <tr>
                        {excelColumns.filter(c => c.visible).map(c => (
                          <th key={c.id} className="px-3 py-2.5 border-r border-green-700/30 font-bold text-white">
                            {c.label.replace('\n', ' ')}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 font-medium">
                      {displayedTransactions.slice(0, 5).map((t, idx) => (
                        <tr key={t.id || idx} className="hover:bg-slate-100/60 bg-white text-slate-700">
                          {excelColumns.filter(c => c.visible).map(c => (
                            <td key={c.id} className="px-3 py-2 border-r border-slate-200 text-xs">
                              {getPreviewCellValue(t, c.id, idx)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

            </div>

            {/* Modal Footer */}
            <div className="bg-slate-100 px-4 py-3 border-t border-slate-200 flex items-center justify-between flex-shrink-0">
              <span className="text-xs text-slate-600 font-medium">
                {excelColumns.filter(c => c.visible).length} of {excelColumns.length} columns will be exported
              </span>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowExcelModal(false)}
                  className="px-4 py-2 bg-white hover:bg-slate-200 border border-slate-300 text-slate-700 text-xs font-bold rounded-lg transition-colors shadow-xs"
                >
                  Cancel
                </button>
                <button
                  onClick={executeExcelDownload}
                  className="px-4 py-2 bg-[#00a884] hover:bg-[#008f72] text-white text-xs font-bold rounded-lg shadow-sm transition-colors flex items-center gap-1.5"
                  data-testid="confirm-download-excel-btn"
                >
                  <DownloadSimple size={16} weight="bold" />
                  Download Excel
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* ─── Standalone Full-Width Chat Overlay ────────────────────────────────────────── */}
      {selectedChatRoomId && (
        <div className="fixed inset-0 z-[9999] bg-white w-full h-full overflow-hidden flex flex-col">
          {isSuperAdmin ? (
            <AdminChat 
              embeddedRoomId={selectedChatRoomId} 
              onBackToList={() => setSelectedChatRoomId(null)} 
            />
          ) : (
            <HandlerChat 
              embeddedRoomId={selectedChatRoomId} 
              onBackToList={() => setSelectedChatRoomId(null)} 
            />
          )}
        </div>
      )}

    </div>
  );
}
